import { Alert, Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePatientsGetById } from '~/backend/connector-next-api/connector-next-components';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { useIsOpeningInViewer, useOpenInViewer } from '~/hooks/openInViewer';
import {
    stageWorkflowFor,
    type OpenIntent,
} from '~/hooks/openInViewer/resolveOpenIntent';
import {
    useDatasetInfo,
    usePatientRetrievals,
    useSetCourseStage,
    useSetPrimaryDataset,
} from '../hooks';
import {
    flattenSeries,
    groupSeriesTreeByDay,
    IMPORT_PROGRESS_POLL_MS,
    seriesComposition,
} from '../model';
import CompositionFooter from './CompositionFooter';
import DatasetDayGroup from './DatasetDayGroup';
import type { DatasetPrimary } from './DatasetRow';
import DrilldownHeader from './DrilldownHeader';
import PatientImportSection from './PatientImportSection';

type PatientDrilldownProps = {
    patientId: string;
    onClose: () => void;
    width: number;
};

/**
 * Docked right-hand card for one patient: identity header plus the
 * patient's datasets (series) grouped by day, each with its details on
 * demand, and a footer with the composition summary and the panel actions.
 * Each row opens itself; the footer's Open takes the whole patient.
 *
 * The panel writes two things about the course and nothing about the data:
 * the course stage (with the stages marked done) and the patient's primary
 * planning image and structure set.
 *
 * Mount with `key={patientId}`: the remount is what guarantees no panel state
 * carries across patients.
 */
export default function PatientDrilldown({
    patientId,
    onClose,
    width,
}: PatientDrilldownProps) {
    const intl = useIntl();
    const retrievals = usePatientRetrievals(patientId);
    // Re-read while series are still arriving, so each one joins the dataset list as it lands
    // rather than when the panel is next opened. Retrievals that cannot be read may still be
    // arriving, so they keep this read going too.
    const { data, isLoading, isError } = usePatientsGetById(
        { pathParams: { id: patientId } },
        {
            refetchInterval:
                retrievals.summary.active || retrievals.isError
                    ? IMPORT_PROGRESS_POLL_MS
                    : false,
        }
    );

    // Focus lands here on open (once per patient via the key remount) and
    // returns to the originating row on close. The panel is still sliding
    // open inside a clipped box at that moment, so the focus must not scroll
    // it into view.
    const rootRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        rootRef.current?.focus({ preventScroll: true });
    }, []);
    const info = useDatasetInfo(rootRef);

    const allSeries = useMemo(() => flattenSeries(data), [data]);
    const groups = useMemo(
        () =>
            groupSeriesTreeByDay(intl, allSeries, {
                imageId: data?.primaryImageId,
                structureSetId: data?.primaryStructureSetId,
            }),
        [intl, allSeries, data?.primaryImageId, data?.primaryStructureSetId]
    );
    const composition = useMemo(
        () => seriesComposition(allSeries),
        [allSeries]
    );
    // Stable while the datasets are, so a hover re-renders only the rows it touches.
    const infoOffer = useMemo(
        () => ({ controls: info.controls, siblings: allSeries }),
        [info.controls, allSeries]
    );

    // Guarded by the detail's version, so a stage set from another tab since
    // this panel loaded conflicts rather than being overwritten.
    const {
        setCourseStage,
        isSaving,
        isError: stageError,
    } = useSetCourseStage(patientId, data?.version ?? undefined);

    // The panel's second write. The primaries steer every later open — the
    // image is registration's pivot and the fallback an Open lands on — so
    // they are the patient's own book-keeping, not this session's preference.
    const {
        setPrimaryDataset,
        isSaving: primarySaving,
        isError: primaryError,
    } = useSetPrimaryDataset(patientId, data?.version ?? undefined);

    // Switching the primary image is confirmed; the pending switch is held
    // here until the user answers. Designating a first image, and every
    // structure-set designation, applies straight away — there is nothing to
    // displace.
    const [pendingImageSwitch, setPendingImageSwitch] = useState<string>();

    const handleTogglePrimary = useCallback<DatasetPrimary['onToggle']>(
        ({ kind, datasetId, isPrimary }) => {
            if (isPrimary) {
                // Clearing is a statement in its own right: the patient has no
                // primary of this kind and the open falls back to its defaults.
                setPrimaryDataset({ kind, datasetId: null });
                return;
            }
            if (kind === 'image' && data?.primaryImageId !== datasetId) {
                // Only when something is displaced. Designating the first
                // image takes nothing away, and a set is never confirmed: it
                // brings its own image along, which is the point of it.
                if (data?.primaryImageId) {
                    setPendingImageSwitch(datasetId);
                    return;
                }
            }
            setPrimaryDataset({ kind, datasetId });
        },
        [setPrimaryDataset, data?.primaryImageId]
    );

    // Memoised because DatasetRow is memoised: a fresh object every render
    // would re-render every row of the panel on any state change at all.
    const primary: DatasetPrimary | undefined = useMemo(
        () =>
            data?.version === undefined
                ? undefined
                : {
                      imageId: data.primaryImageId ?? null,
                      structureSetId: data.primaryStructureSetId ?? null,
                      onToggle: handleTogglePrimary,
                      busy: primarySaving,
                  },
        [
            data?.version,
            data?.primaryImageId,
            data?.primaryStructureSetId,
            handleTogglePrimary,
            primarySaving,
        ]
    );

    /**
     * The clicked dataset's display name, for the switch confirmation.
     *
     * The confirmation names only this one. Two series can share a
     * description — two reconstructions, two phases — so a message naming
     * both the outgoing and the incoming image would read as one image
     * replacing itself. The one being replaced is whichever row wears the
     * filled star, in view behind the dialog — which is why the message
     * points at the star rather than at a name or a position.
     */
    const nameOf = (datasetId: string | undefined): string =>
        allSeries.find((series) => series.id === datasetId)
            ?.seriesDescription || intl.formatMessage({ id: 'series' });

    const openInViewer = useOpenInViewer();
    const openBusy = useIsOpeningInViewer();
    // The footer's Open takes the whole patient. Choosing *which* dataset is
    // the row's own button, one click on the thing itself, rather than a
    // selection to assemble first and act on second.
    const handleOpen = useCallback(() => {
        openInViewer(patientId, 'all');
    }, [openInViewer, patientId]);
    // Open needs the loaded detail to resolve datasets from.
    const canOpen = data !== undefined && !openBusy;
    const openLabel = intl.formatMessage({ id: 'patientList.drilldown.open' });

    // The workflow the course stage leads to; the row menu offers its action
    // first, since a user is most often doing what the course says they are.
    const stageWorkflow = stageWorkflowFor(data?.courseStage);

    // Opening one dataset states the intent; the resolver fills the rest from
    // it, so a structure set brings the image it was contoured on rather than
    // whichever image a heuristic would have preferred.
    const handleOpenObject = useCallback(
        (intent: OpenIntent) => openInViewer(patientId, 'all', intent),
        [openInViewer, patientId]
    );

    return (
        <Box
            ref={rootRef}
            tabIndex={-1}
            role="region"
            aria-label={intl.formatMessage({
                id: 'patientList.drilldown.region',
            })}
            sx={{
                width,
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                overflow: 'hidden',
            }}
        >
            <DrilldownHeader
                patient={data}
                onClose={onClose}
                onSetCourseStage={setCourseStage}
                stageSaving={isSaving}
            />
            {stageError && (
                <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                    <FormattedMessage id="patientList.stage.saveFailed" />
                </Alert>
            )}
            {primaryError && (
                <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                    <FormattedMessage id="patientList.primary.saveFailed" />
                </Alert>
            )}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <PatientImportSection retrievals={retrievals} />
                {isLoading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {isError && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        <FormattedMessage id="patientList.drilldown.error" />
                    </Alert>
                )}
                {/* A patient whose requested series have not arrived yet is not an
                    empty record: the import section above says what is coming. Until
                    the retrievals have been read, nothing says the record is empty. */}
                {!isLoading &&
                    !isError &&
                    !retrievals.isLoading &&
                    !retrievals.isError &&
                    allSeries.length === 0 &&
                    retrievals.summary.total === 0 && (
                        <Alert severity="info" sx={{ m: 2 }}>
                            <FormattedMessage id="patientList.drilldown.emptyAll" />
                        </Alert>
                    )}
                {groups.map((group) => (
                    <DatasetDayGroup
                        key={group.day}
                        group={group}
                        mode="open"
                        onOpenObject={handleOpenObject}
                        stageWorkflow={stageWorkflow}
                        openBusy={openBusy}
                        primary={primary}
                        info={infoOffer}
                        infoOpenKey={info.openKey}
                        infoPinnedKey={info.pinnedKey}
                    />
                ))}
            </Box>

            <ConfirmationDialog
                open={pendingImageSwitch !== undefined}
                mode="primary"
                title={intl.formatMessage({
                    id: 'patientList.primary.image.switchTitle',
                })}
                message={
                    intl.formatMessage(
                        { id: 'patientList.primary.image.switchMessage' },
                        { name: nameOf(pendingImageSwitch) }
                    ) +
                    // Stated only when there is one to lose. The set is
                    // contoured on the image being replaced, so it cannot
                    // survive the switch — and losing it silently would be a
                    // second change the user did not ask for.
                    (data?.primaryStructureSetId
                        ? intl.formatMessage({
                              id: 'patientList.primary.image.switchDropsSet',
                          })
                        : '')
                }
                confirmText={intl.formatMessage({
                    id: 'patientList.primary.image.switchConfirm',
                })}
                onCancel={() => setPendingImageSwitch(undefined)}
                onConfirm={() => {
                    if (pendingImageSwitch !== undefined) {
                        setPrimaryDataset({
                            kind: 'image',
                            datasetId: pendingImageSwitch,
                        });
                    }
                    setPendingImageSwitch(undefined);
                }}
            />

            <CompositionFooter
                summary={
                    data && {
                        composition,
                        studyCount: data.studies?.length ?? 0,
                        datasetCount: allSeries.length,
                    }
                }
                actions={
                    <>
                        <DisabledHint
                            title={intl.formatMessage({
                                id: 'patientList.drilldown.exportHint',
                            })}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<Download size={14} />}
                                disabled
                            >
                                <FormattedMessage id="patientList.drilldown.export" />
                            </Button>
                        </DisabledHint>
                        {canOpen ? (
                            <Tooltip
                                // Describes the button; its name stays the visible label.
                                describeChild
                                title={intl.formatMessage({
                                    id: 'patientList.openHint',
                                })}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleOpen}
                                >
                                    {openLabel}
                                </Button>
                            </Tooltip>
                        ) : (
                            <DisabledHint
                                title={intl.formatMessage({
                                    id: openBusy
                                        ? 'patientList.open.inFlight'
                                        : 'patientList.drilldown.openUnavailableHint',
                                })}
                            >
                                <Button variant="contained" disabled>
                                    {openLabel}
                                </Button>
                            </DisabledHint>
                        )}
                    </>
                }
            />
        </Box>
    );
}
